import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarRange, Coins, CloudSun, Plane, StickyNote, MessageCircle } from "lucide-react";
import Seo from "../components/Seo";
import InquiryForm from "../components/InquiryForm";
import { useSite } from "../components/SiteContext";
import {
  Breadcrumbs, EditorialCard, FaqList, Gallery, Grid, Img, Loading, PageHero, Reviews,
  RichText, Section, SectionHead,
} from "../components/shared";
import { getItem, getList, destUrl, inr, waLink } from "../lib/api";
import { fadeUp, stagger, containerViewport } from "../lib/motion";

const RATING_BADGE = {
  Peak: "bg-[rgba(31,111,91,0.14)] text-[#1F6F5B]",
  Avoid: "bg-[rgba(180,35,24,0.12)] text-[#B42318]",
};
const DEFAULT_RATING_BADGE = "bg-[rgba(176,141,87,0.16)] text-[var(--brass-2)]";
const ratingBadgeClass = (rating) => RATING_BADGE[rating] || DEFAULT_RATING_BADGE;

const Block = ({ id, eyebrow, title, children }) => (
  <div id={id} className="scroll-mt-28 border-t border-[var(--line)] pt-10">
    {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
    <h2 className="font-display text-[1.85rem] leading-tight text-[var(--ink)] sm:text-[2.1rem]">
      {title}
    </h2>
    <div className="mt-5">{children}</div>
  </div>
);

export default function DestinationDetail({ region }) {
  const { slug } = useParams();
  const { settings } = useSite();
  const [d, setD] = useState(null);
  const [related, setRelated] = useState([]);
  const [exps, setExps] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    setD(null);
    setError(false);
    getItem("destinations", slug)
      .then((doc) => {
        setD(doc);
        if (doc.related?.length)
          getList("destinations", { slugs: doc.related.join(",") })
            .then((r) => setRelated(r.items || []))
            .catch(() => {});
        if (doc.experience_slugs?.length)
          getList("experiences", { slugs: doc.experience_slugs.join(",") })
            .then((r) => setExps(r.items || []))
            .catch(() => {});
      })
      .catch(() => setError(true));
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (error)
    return (
      <Section testId="destination-not-found">
        <Seo title="Destination Not Found | Azulo Travel" description="This destination is unavailable. Explore our current India and international journeys." noIndex />
        <h1 className="font-display text-4xl">Destination not found</h1>
        <Link to="/international" className="mt-4 inline-block text-[var(--brass-2)]">
          Browse all destinations
        </Link>
      </Section>
    );
  if (!d) return <Loading label="Loading destination" />;

  const regionLabel = d.region === "india" ? "India" : "International";
  const crumbs = [
    { label: "Home", to: "/" },
    { label: regionLabel, to: `/${d.region}` },
    { label: d.title },
  ];

  return (
    <>
      <Seo
        title={d.meta_title || `${d.title} | Azulo Travel`}
        description={d.meta_description || d.tagline}
        image={d.og_image || d.hero_image}
        path={destUrl(d)}
        record={d}
        keywords={d.keywords || []}
        noIndex={d.no_index}
        breadcrumbs={crumbs}
        schema={{
          "@context": "https://schema.org",
          "@type": "TouristDestination",
          name: d.title,
          description: d.meta_description || d.tagline,
          image: d.hero_image,
          touristType: (d.styles || []).join(", "),
        }}
      />

      <PageHero
        image={d.hero_image}
        focus={d.hero_focus}
        eyebrow={`${regionLabel} · ${d.country}`}
        title={d.seo_heading || d.title}
        subtitle={d.tagline}
      >
        <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/85">
          {d.duration_note && <span data-testid="hero-duration">{d.duration_note}</span>}
          {d.starting_price_inr ? (
            <span data-testid="hero-price">From {inr(d.starting_price_inr)} per person</span>
          ) : null}
          {d.currency && <span>{d.currency}</span>}
        </div>
      </PageHero>

      <Section testId="destination-detail">
        <Breadcrumbs items={crumbs} />
        <div className="grid gap-12 lg:grid-cols-[1.55fr_1fr] lg:gap-16">
          <div className="min-w-0 space-y-10">
            <div>
              <p className="eyebrow mb-3">Overview</p>
              <h2 className="sr-only">Overview</h2>
              <RichText text={d.overview} />
            </div>

            {d.best_time?.length > 0 && (
              <Block id="best-time" eyebrow="When to go" title="Best time to visit">
                {d.best_time_note && (
                  <p className="mb-6 text-[0.95rem] leading-relaxed text-[var(--muted-ink)]">
                    {d.best_time_note}
                  </p>
                )}
                <div data-testid="best-time-table" className="overflow-hidden rounded-xl border border-[var(--line)]">
                  {d.best_time.map((b, i) => (
                    <div
                      key={b.season || i}
                      className={`grid gap-1 p-4 sm:grid-cols-[1fr_1fr_auto] sm:items-center sm:gap-4 ${
                        i % 2 ? "bg-[var(--surface-warm)]" : "bg-[var(--surface)]"
                      }`}
                    >
                      <div>
                        <p className="text-sm font-semibold text-[var(--ink)]">{b.season}</p>
                        <p className="text-xs text-[var(--muted-ink)]">{b.months}</p>
                      </div>
                      <p className="text-xs leading-relaxed text-[var(--muted-ink)]">{b.note}</p>
                      <span
                        className={`w-fit rounded-full px-3 py-1 text-[0.65rem] font-bold uppercase tracking-wider ${ratingBadgeClass(
                          b.rating
                        )}`}
                      >
                        {b.rating}
                      </span>
                    </div>
                  ))}
                </div>
              </Block>
            )}

            <Block id="essentials" eyebrow="Practical" title="Visa, currency, weather and flights">
              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="show"
                viewport={containerViewport}
                className="grid gap-4 sm:grid-cols-2"
              >
                {[
                  [StickyNote, "Visa", d.visa_info, d.visa_slug ? `/visa/${d.visa_slug}` : null, "visa-info"],
                  [Coins, "Currency", `${d.currency}. ${d.currency_note || ""}`, null, "currency-info"],
                  [CloudSun, "Weather", d.weather_note, null, "weather-info"],
                  [Plane, "Flights", d.flight_info, null, "flight-info"],
                ].map(([I, label, body, link, tid]) =>
                  body ? (
                    <motion.div
                      key={label}
                      variants={fadeUp}
                      data-testid={tid}
                      className="rounded-xl border border-[var(--line)] bg-[var(--surface)] p-5"
                    >
                      <div className="mb-3 flex items-center gap-2.5">
                        <I className="h-4 w-4 text-[var(--brass)]" />
                        <p className="text-sm font-semibold text-[var(--ink)]">{label}</p>
                      </div>
                      <p className="text-[0.85rem] leading-relaxed text-[var(--muted-ink)]">{body}</p>
                      {link && (
                        <Link
                          to={link}
                          data-testid={`${tid}-link`}
                          className="mt-3 inline-block text-xs font-semibold text-[var(--brass-2)] hover:underline"
                        >
                          Full visa guide &rarr;
                        </Link>
                      )}
                    </motion.div>
                  ) : null
                )}
              </motion.div>

              {d.weather?.length > 0 && (
                <div className="mt-5 overflow-x-auto rounded-xl border border-[var(--line)]">
                  <table data-testid="weather-table" className="w-full text-left text-sm">
                    <thead className="bg-[var(--ink)] text-white">
                      <tr>
                        <th className="p-3 text-xs font-semibold uppercase tracking-wider">Period</th>
                        <th className="p-3 text-xs font-semibold uppercase tracking-wider">Temperature</th>
                        <th className="p-3 text-xs font-semibold uppercase tracking-wider">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.weather.map((w, i) => (
                        <tr key={w.period || i} className={i % 2 ? "bg-[var(--surface-warm)]" : "bg-[var(--surface)]"}>
                          <td className="p-3 font-medium text-[var(--ink)]">{w.period}</td>
                          <td className="p-3 text-[var(--muted-ink)]">{w.temp}</td>
                          <td className="p-3 text-[var(--muted-ink)]">{w.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {d.flight_rows?.length > 0 && (
                <div className="mt-4 overflow-x-auto rounded-xl border border-[var(--line)]">
                  <table data-testid="flight-table" className="w-full text-left text-sm">
                    <tbody>
                      {d.flight_rows.map((f, i) => (
                        <tr key={f.route || i} className={i % 2 ? "bg-[var(--surface-warm)]" : "bg-[var(--surface)]"}>
                          <td className="p-3 font-medium text-[var(--ink)]">{f.route}</td>
                          <td className="p-3 text-[var(--muted-ink)]">{f.duration}</td>
                          <td className="p-3 text-[var(--muted-ink)]">{f.airlines}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Block>

            {d.hotels?.length > 0 && (
              <Block id="hotels" eyebrow="Where to stay" title="Hotels we recommend">
                <div data-testid="hotels-list" className="space-y-4">
                  {d.hotels.map((h, i) => (
                    <div
                      key={h.name || i}
                      data-testid={`hotel-item-${i}`}
                      className="flex flex-col gap-4 overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--surface)] sm:flex-row"
                    >
                      <div className="sm:w-52 sm:shrink-0">
                        <Img src={h.image} alt={h.name} ratio="4/3" />
                      </div>
                      <div className="p-5 sm:pl-0">
                        <p className="eyebrow mb-1.5">
                          {h.city} &middot; {h.category}
                        </p>
                        <h3 className="font-display text-xl text-[var(--ink)]">{h.name}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-[var(--muted-ink)]">{h.note}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Block>
            )}

            {d.things_to_do?.length > 0 && (
              <Block id="things-to-do" eyebrow="Highlights" title="Things to do">
                <Grid cols={2}>
                  {d.things_to_do.map((t, i) => (
                    <motion.div
                      key={t.title || i}
                      variants={fadeUp}
                      data-testid={`thing-to-do-${i}`}
                      className="az-card group"
                    >
                      <Img src={t.image} alt={t.title} ratio="16/10" />
                      <div className="p-5">
                        <h3 className="font-display text-lg text-[var(--ink)]">{t.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-[var(--muted-ink)]">
                          {t.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </Grid>
              </Block>
            )}

            {exps.length > 0 && (
              <Block id="experiences" eyebrow="Bookable now" title="Experiences here">
                <Grid cols={2}>
                  {exps.map((e) => (
                    <EditorialCard
                      key={e.slug}
                      to={`/experiences/${e.slug}`}
                      image={e.card_image || e.hero_image}
                      title={e.title}
                      meta={`${e.duration} · ${inr(e.price_inr)}`}
                      subtitle={e.tagline}
                      testId={`linked-experience-${e.slug}`}
                    />
                  ))}
                </Grid>
              </Block>
            )}

            {d.itinerary?.length > 0 && (
              <Block id="itinerary" eyebrow="Suggested route" title={d.itinerary_title || "Suggested itinerary"}>
                <ol data-testid="itinerary-list" className="space-y-0">
                  {d.itinerary.map((it, i) => (
                    <li
                      key={it.day ?? i}
                      data-testid={`itinerary-day-${i}`}
                      className="flex gap-5 border-b border-[var(--line)] py-5 last:border-0"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(176,141,87,0.14)] text-xs font-bold text-[var(--brass-2)]">
                        {it.day}
                      </span>
                      <div>
                        <h3 className="font-display text-lg text-[var(--ink)]">{it.title}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-[var(--muted-ink)]">{it.detail}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </Block>
            )}

            {d.gallery?.length > 0 && (
              <Block id="gallery" eyebrow="Gallery" title={`${d.title} in pictures`}>
                <Gallery images={d.gallery} alt={d.title} testId="destination-gallery" />
              </Block>
            )}

            {d.faqs?.length > 0 && (
              <Block id="faqs" eyebrow="Questions" title="Frequently asked">
                <FaqList faqs={d.faqs} testId="destination-faq" />
              </Block>
            )}

            {d.reviews?.length > 0 && (
              <Block id="reviews" eyebrow="Guest stories" title="What our travellers say">
                <Reviews reviews={d.reviews} testId="destination-review" />
              </Block>
            )}
          </div>

          <aside className="lg:sticky lg:top-24 lg:h-fit">
            <InquiryForm
              source="destination"
              sourceRef={d.slug}
              sourceTitle={d.title}
              destination={d.title}
              title={`Plan your ${d.title} journey`}
              subtitle="A designer who has travelled here will reply within one business day."
            />
            <a
              href={waLink(
                settings.whatsapp,
                `Hello Azulo Travel, I would like to plan a journey to ${d.title}.`
              )}
              target="_blank"
              rel="noreferrer noopener"
              data-destination={d.title}
              data-testid="destination-whatsapp-button"
              className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-[var(--line-strong)] bg-[var(--surface)] px-5 py-3.5 text-sm font-semibold text-[var(--ink)] transition-colors hover:border-[var(--brass)] hover:text-[var(--brass-2)]"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp a travel designer
            </a>
            {d.best_time_note && (
              <div className="mt-4 rounded-xl border border-[var(--line)] bg-[var(--surface-warm)] p-5">
                <div className="mb-2 flex items-center gap-2">
                  <CalendarRange className="h-4 w-4 text-[var(--brass)]" />
                  <p className="text-xs font-bold uppercase tracking-wider text-[var(--ink)]">
                    Best time
                  </p>
                </div>
                <p className="text-[0.8rem] leading-relaxed text-[var(--muted-ink)]">
                  {d.best_time_note}
                </p>
              </div>
            )}
          </aside>
        </div>
      </Section>

      {related.length > 0 && (
        <Section wash testId="related-destinations">
          <SectionHead eyebrow="You may also like" title="Related destinations" />
          <Grid cols={3}>
            {related.map((r) => (
              <EditorialCard
                key={r.slug}
                to={destUrl(r)}
                image={r.card_image || r.hero_image}
                title={r.title}
                meta={r.country}
                subtitle={r.tagline}
                testId={`related-destination-${r.slug}`}
              />
            ))}
          </Grid>
        </Section>
      )}
    </>
  );
}
